import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { stringifyDetail } from '../lib/utils';

const API_BASE = window.location.origin;

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setError('Missing reset token. Please use the link from your email.');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, new_password: password })
            });
            const data = await res.json();
            if (!res.ok) {
                setError(stringifyDetail(data.detail) || 'Failed to reset password. The link may have expired.');
                return;
            }
            setSuccess(true);
        } catch {
            setError('Unable to connect. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <button
                    onClick={() => navigate('/login')}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
                    data-testid="back-to-login"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to login
                </button>

                <div className="bg-background rounded-2xl border p-8 shadow-sm">
                    {success ? (
                        <div className="text-center" data-testid="reset-success">
                            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                            </div>
                            <h1 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Password Reset
                            </h1>
                            <p className="text-sm text-muted-foreground mb-6">
                                Your password has been changed successfully.
                            </p>
                            <Button
                                onClick={() => navigate('/login')}
                                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="go-to-login-btn"
                            >
                                Go to Login
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Lock className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Set New Password
                                    </h1>
                                    <p className="text-xs text-muted-foreground">
                                        Choose a strong password for your account
                                    </p>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 mb-4 flex items-start gap-2" data-testid="reset-error">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="new-password" className="text-sm">New Password</Label>
                                    <div className="relative mt-1">
                                        <Input
                                            id="new-password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="At least 6 characters"
                                            required
                                            minLength={6}
                                            data-testid="new-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="confirm-password" className="text-sm">Confirm Password</Label>
                                    <Input
                                        id="confirm-password"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Re-enter your password"
                                        required
                                        className="mt-1"
                                        data-testid="confirm-password-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading || !token || !password || !confirmPassword}
                                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
                                    data-testid="reset-submit-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Lock className="w-4 h-4 mr-2" />
                                    )}
                                    Reset Password
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
